import type {
  PaginatedResponse,
  Job,
  JobWithCoordinators,
  Candidate,
  CandidateWithApplications,
  Application,
  ApplicationWithDetails,
  Review,
  ReviewListItem,
  ReviewWithDetails,
  ApiError,
  ConfigResponse,
  ConfigData,
  ValidationResult,
  SaveConfigResponse,
  DashboardMetricsResponse,
  DeadLetterEntry,
  DeadLetterStats,
  DeadLetterListParams,
  SetupVerifyResponse,
  SystemHealthResponse,
  AiCostMetrics,
  AICostDashboardData,
  FeedbackType,
  FeedbackEntry,
  SubmitFeedbackResponse,
  ReviewVersionsResponse,
  RollbackResponse,
  Person,
  PersonDetail,
  NoteType,
  InterviewNotesResponse,
  CreateNoteResponse,
  SyncStatusResponse,
  GdprDeletionCertificate,
  DataRetentionDashboardResponse,
  DataRetentionAuditEntry,
  DataRetentionSummaryResponse,
  ReviewFeedbackEntry,
  ReviewFeedbackData,
  QuickRating,
  TalentPoolCandidate,
  TalentPoolStats,
  ConvertToApplicationResponse,
  ReportType,
  ReportSnapshotSummary,
  ReportSnapshotFull,
  ReportGenerateResponse,
  DashboardAnalytics,
  ScreeningConflictsResponse,
  ApplicationMessagesResponse,
  AiAccuracyReportResponse,
  FunnelInsights,
  ModelCatalogItem,
  ModelCatalogResponse,
  ProviderCredentialMeta,
  ProviderPolicy,
  SourcingMatchesResponse,
} from '../types';

export interface DashboardJob {
  id: number;
  title: string;
  department: string | null;
  location: string | null;
  status: string;
  srStatus: string | null;
  applicationCount: number;
  reviewedTodayCount: number;
  stageCounts?: {
    LEAD: number;
    SCREENING: number;
    INTERVIEW: number;
    OFFER: number;
  };
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Request failed',
        statusCode: response.status,
      }));
      // Extract a human-readable error message, handling cases where error might be an object
      const errorMsg =
        typeof errorData.error === 'string'
          ? errorData.error
          : typeof errorData.message === 'string'
            ? errorData.message
            : errorData.error?.message || `HTTP ${response.status}`;
      // US-404: SourcingTuneEntry needs to tell "agent already exists" (409,
      // handle idempotently) apart from a genuine failure (show an error) —
      // the message string alone isn't a reliable discriminator, so the real
      // HTTP status rides along on the thrown Error itself.
      const error = new Error(errorMsg) as Error & { statusCode?: number; body?: unknown };
      error.statusCode = response.status;
      // US-403: the sourcing-matches 409 carries `currentSpecVersion` in its
      // body, which the paginating consumer needs to re-sync to. The message
      // string alone can't carry it, so the parsed body rides along too.
      error.body = errorData;
      throw error;
    }

    return response.json();
  }

  // Jobs API

  async getJobs(
    page: number = 1,
    limit: number = 20,
    search?: string,
    status?: string
  ): Promise<PaginatedResponse<Job>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    return this.request(`/api/jobs?${params.toString()}`);
  }

  async getJob(id: number): Promise<JobWithCoordinators> {
    return this.request(`/api/jobs/${id}`);
  }

  async triggerJobSync(id: number): Promise<{ message: string; jobId: number }> {
    return this.request(`/api/jobs/${id}/sync`, { method: 'POST' });
  }

  async getBackfillPreview(id: number): Promise<{
    jobId: number;
    srJobId: string;
    unreviewedCount: number;
    backfillInProgress: boolean;
  }> {
    return this.request(`/api/jobs/${id}/backfill/preview`);
  }

  async startBackfill(id: number): Promise<{
    message: string;
    jobId: number;
    srJobId: string;
    queuedCount: number;
  }> {
    return this.request(`/api/jobs/${id}/backfill`, { method: 'POST' });
  }

  async cancelBackfill(id: number): Promise<{ message: string; jobId: number }> {
    return this.request(`/api/jobs/${id}/backfill`, { method: 'DELETE' });
  }

  async updateJob(
    id: number,
    data: { status?: string; allowedStages?: string[]; skipHumanReviewed?: boolean }
  ): Promise<JobWithCoordinators> {
    return this.request(`/api/jobs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateSourcingSettings(
    jobId: number,
    data: { passiveSourcingEnabled?: boolean; sourcingMinStars?: number }
  ): Promise<{ passiveSourcingEnabled: boolean; sourcingMinStars: number }> {
    return this.request(`/api/jobs/${jobId}/sourcing-settings`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getSourcingMatches(
    jobId: number,
    params: { limit?: number; offset?: number; minStars?: number; specVersion?: number } = {}
  ): Promise<SourcingMatchesResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    if (params.minStars !== undefined) qs.set('minStars', String(params.minStars));
    if (params.specVersion !== undefined) qs.set('specVersion', String(params.specVersion));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/api/jobs/${jobId}/sourcing-matches${suffix}`);
  }

  async getJobDeletePreview(id: number): Promise<{
    jobId: number;
    title: string;
    applicationCount: number;
    reviewCount: number;
    canDelete: boolean;
    reason?: string;
  }> {
    return this.request(`/api/jobs/${id}/delete-preview`);
  }

  async deleteJob(id: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/jobs/${id}`, { method: 'DELETE' });
  }

  // Job Management

  async discoverJobs(): Promise<{
    message: string;
    jobsSynced: number;
    totalActiveJobs: number;
  }> {
    return this.request('/api/jobs/discover', { method: 'POST', body: JSON.stringify({}) });
  }

  // Application Management

  async archiveApplication(id: number): Promise<{
    success: boolean;
    message: string;
    applicationId: number;
    previousStatus: string;
  }> {
    return this.request(`/api/applications/${id}`, { method: 'DELETE' });
  }

  /** US-139: Fetch HireLoop interviewer notes for an application on-demand */
  async getApplicationMessages(id: number): Promise<ApplicationMessagesResponse> {
    return this.request(`/api/applications/${id}/messages`);
  }

  // People Management

  async archivePerson(id: number): Promise<{
    success: boolean;
    message: string;
    candidateId: number;
    archivedCount: number;
  }> {
    return this.request(`/api/people/${id}`, { method: 'DELETE' });
  }

  async reprocessPerson(id: number): Promise<{
    success: boolean;
    message: string;
    candidateId: number;
    queuedCount: number;
  }> {
    return this.request(`/api/people/${id}/reprocess`, { method: 'POST' });
  }

  async batchArchivePeople(candidateIds: number[]): Promise<{
    success: boolean;
    message: string;
    archivedCount: number;
    results: Array<{ candidateId: number; archivedApplications: number }>;
  }> {
    return this.request('/api/people/batch-archive', {
      method: 'POST',
      body: JSON.stringify({ candidateIds }),
    });
  }

  async batchReprocessPeople(candidateIds: number[]): Promise<{
    success: boolean;
    message: string;
    queuedCount: number;
    results: Array<{ candidateId: number; queuedApplications: number }>;
  }> {
    return this.request('/api/people/batch-reprocess', {
      method: 'POST',
      body: JSON.stringify({ candidateIds }),
    });
  }

  // Application batch operations

  async batchArchiveApplications(applicationIds: number[]): Promise<{
    success: boolean;
    message: string;
    archivedCount: number;
  }> {
    return this.request('/api/applications/batch-archive', {
      method: 'POST',
      body: JSON.stringify({ applicationIds }),
    });
  }

  async batchReprocessApplications(applicationIds: number[]): Promise<{
    success: boolean;
    message: string;
    queuedCount: number;
  }> {
    return this.request('/api/applications/batch-reprocess', {
      method: 'POST',
      body: JSON.stringify({ applicationIds }),
    });
  }

  // People API (person-centric view)

  async getPeople(
    page: number = 1,
    limit: number = 20,
    search?: string,
    status?: string,
    jobId?: number,
    minScore?: number,
    maxScore?: number
  ): Promise<PaginatedResponse<Person>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (jobId !== undefined) params.set('jobId', String(jobId));
    if (minScore !== undefined) params.set('minScore', String(minScore));
    if (maxScore !== undefined) params.set('maxScore', String(maxScore));
    return this.request(`/api/people?${params.toString()}`);
  }

  async getPerson(id: number): Promise<PersonDetail> {
    return this.request(`/api/people/${id}`);
  }

  // Interview Notes API

  async getPersonNotes(personId: number): Promise<InterviewNotesResponse> {
    return this.request(`/api/people/${personId}/notes`);
  }

  async createPersonNote(
    personId: number,
    content: string,
    noteType: NoteType,
    applicationId?: number
  ): Promise<CreateNoteResponse> {
    return this.request(`/api/people/${personId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content, noteType, applicationId }),
    });
  }

  async deletePersonNote(personId: number, noteId: number): Promise<{ success: boolean; deletedId: number }> {
    return this.request(`/api/people/${personId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  // Candidates API

  async getCandidates(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<PaginatedResponse<Candidate>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    return this.request(`/api/candidates?${params.toString()}`);
  }

  async getCandidate(id: number): Promise<CandidateWithApplications> {
    return this.request(`/api/candidates/${id}`);
  }

  // Talent Pool API (US-088)

  async getTalentPool(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<PaginatedResponse<TalentPoolCandidate>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    return this.request(`/api/candidates/talent-pool?${params.toString()}`);
  }

  async getTalentPoolStats(): Promise<TalentPoolStats> {
    return this.request('/api/candidates/talent-pool/stats');
  }

  async createTalentPoolCandidate(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    location?: string;
    notes?: string;
  }): Promise<TalentPoolCandidate> {
    return this.request('/api/candidates/talent-pool', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async convertToApplication(
    candidateId: number,
    jobId: number
  ): Promise<ConvertToApplicationResponse> {
    return this.request(`/api/candidates/${candidateId}/convert-to-application`, {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    });
  }

  // Applications API

  async getApplications(
    page: number = 1,
    limit: number = 20,
    search?: string,
    status?: string,
    jobId?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    stages?: string
  ): Promise<PaginatedResponse<Application>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (jobId !== undefined) params.set('jobId', String(jobId));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    if (stages) params.set('stages', stages);
    return this.request(`/api/applications?${params.toString()}`);
  }

  async getApplication(id: number): Promise<ApplicationWithDetails> {
    return this.request(`/api/applications/${id}`);
  }

  async forceReview(
    id: number
  ): Promise<{ message: string; applicationId: number }> {
    return this.request(`/api/applications/${id}/force-review`, {
      method: 'POST',
    });
  }

  async reprocessApplication(
    id: number,
    reason?: string
  ): Promise<{ message: string; applicationId: number }> {
    return this.request(`/api/applications/${id}/reprocess`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async lookupBySrId(srApplicationId: string): Promise<
    | { found: true; applicationId: number; candidateId: number; jobId: number }
    | { found: false; srApplicationId: string }
  > {
    // Use a raw fetch here because the API returns 404 with {found:false} for
    // "not in DB yet" — a valid outcome, not an error. The shared request()
    // helper throws on any non-2xx, which would set isError and show the wrong
    // UI path ("connection error" instead of the ingest prompt).
    const url = `${this.baseUrl}/api/applications/by-sr-id/${encodeURIComponent(srApplicationId)}`;
    const response = await fetch(url, { credentials: 'include' });
    if (response.status === 404) {
      return response.json();
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  }

  async forceReviewBySrId(
    srApplicationId: string,
    opts: { mode?: 'fetch' | 'best-effort' } = {}
  ): Promise<{ applicationId?: number; trackingId?: string; mode: string }> {
    const qs = opts.mode ? `?mode=${opts.mode}` : '';
    return this.request(
      `/api/applications/by-sr-id/${encodeURIComponent(srApplicationId)}/force-review${qs}`,
      { method: 'POST' }
    );
  }

  async reprocessBySrId(
    srApplicationId: string,
    opts: { mode?: 'fetch' | 'best-effort'; reason?: string } = {}
  ): Promise<{ applicationId?: number; trackingId?: string; mode: string }> {
    const qs = opts.mode ? `?mode=${opts.mode}` : '';
    return this.request(
      `/api/applications/by-sr-id/${encodeURIComponent(srApplicationId)}/reprocess${qs}`,
      { method: 'POST', body: JSON.stringify({ reason: opts.reason }) }
    );
  }

  async updateSponsorshipPolicy(
    jobId: number,
    sponsorshipOffered: 'yes' | 'no' | 'unspecified'
  ): Promise<{ id: number; sponsorshipOffered: string }> {
    return this.request(`/api/jobs/${jobId}/sponsorship-policy`, {
      method: 'PATCH',
      body: JSON.stringify({ sponsorshipOffered }),
    });
  }

  async getJobApplications(
    jobId: number,
    page: number = 1,
    limit: number = 20,
    sortBy: string = 'createdAt',
    sortDir: string = 'desc'
  ): Promise<PaginatedResponse<Application>> {
    return this.request(
      `/api/jobs/${jobId}/applications?page=${page}&limit=${limit}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
  }

  // Feedback API

  async submitFeedback(
    applicationId: number,
    feedbackType: FeedbackType,
    content: string
  ): Promise<SubmitFeedbackResponse> {
    return this.request(`/api/applications/${applicationId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedbackType, content }),
    });
  }

  async getFeedback(
    applicationId: number,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ data: FeedbackEntry[]; count: number }> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return this.request(`/api/applications/${applicationId}/feedback?${params.toString()}`);
  }

  // Review Feedback API (inline dashboard feedback)

  async submitReviewFeedback(
    reviewId: number,
    data: ReviewFeedbackData
  ): Promise<{ success: boolean; feedbackId: number }> {
    return this.request(`/api/feedback/review/${reviewId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReviewFeedback(
    reviewId: number
  ): Promise<{ data: ReviewFeedbackEntry[]; reviewId: number; applicationId: number }> {
    return this.request(`/api/feedback/review/${reviewId}`);
  }

  async getQuickRate(
    applicationId: number
  ): Promise<{ rating: QuickRating | null }> {
    return this.request(`/api/applications/${applicationId}/quick-rate`);
  }

  async submitQuickRate(
    applicationId: number,
    rating: QuickRating
  ): Promise<{ success: boolean; rating: QuickRating }> {
    return this.request(`/api/applications/${applicationId}/quick-rate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
  }

  async submitSourcingFeedback(
    jobId: number,
    candidateId: number,
    feedback: 'approved' | 'rejected'
  ): Promise<{ feedback: string; feedbackAt: string }> {
    return this.request(`/api/jobs/${jobId}/sourcing-matches/${candidateId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  }

  async getReviewVersions(applicationId: number): Promise<ReviewVersionsResponse> {
    return this.request(`/api/applications/${applicationId}/review-versions`);
  }

  async rollbackReview(applicationId: number, reviewId: number): Promise<RollbackResponse> {
    return this.request(`/api/applications/${applicationId}/reviews/${reviewId}/rollback`, {
      method: 'POST',
    });
  }

  // Reviews API

  async getReviews(
    page: number = 1,
    limit: number = 20,
    search?: string,
    recommendation?: number,
    minScore?: number,
    maxScore?: number,
    hasScreeningKnockout?: boolean,
    staleFlag?: boolean
  ): Promise<PaginatedResponse<ReviewListItem>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.set('search', search);
    if (recommendation !== undefined) params.set('recommendation', String(recommendation));
    if (minScore !== undefined) params.set('minScore', String(minScore));
    if (maxScore !== undefined) params.set('maxScore', String(maxScore));
    if (hasScreeningKnockout) params.set('hasScreeningKnockout', 'true');
    if (staleFlag) params.set('staleFlag', 'true');
    return this.request(`/api/reviews?${params.toString()}`);
  }

  async getReview(id: number): Promise<ReviewWithDetails> {
    return this.request(`/api/reviews/${id}`);
  }

  async getApplicationReviews(
    applicationId: number
  ): Promise<{ data: Review[] }> {
    return this.request(`/api/applications/${applicationId}/reviews`);
  }

  // Sync API

  async getSyncStatus(reviewId: number): Promise<SyncStatusResponse> {
    return this.request(`/api/reviews/${reviewId}/sync-status`);
  }

  async syncReview(
    reviewId: number,
    force: boolean = false
  ): Promise<{ message: string; reviewId: number; applicationId: number }> {
    return this.request(`/api/reviews/${reviewId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  async syncApplication(
    applicationId: number,
    force: boolean = false
  ): Promise<{ message: string; applicationId: number; reviewId: number }> {
    return this.request(`/api/applications/${applicationId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  async toggleJobSync(
    jobId: number,
    enabled: boolean
  ): Promise<{ jobId: number; syncEnabled: boolean; message: string }> {
    return this.request(`/api/jobs/${jobId}/sync`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  // Health Check

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  // Dashboard Metrics

  async getDashboardMetrics(): Promise<DashboardMetricsResponse> {
    return this.request('/api/dashboard/metrics');
  }

  async getScreeningConflicts(): Promise<ScreeningConflictsResponse> {
    return this.request('/api/dashboard/screening-conflicts');
  }

  async overrideScreeningKnockout(reviewId: number, overrideReason?: string): Promise<{ success: boolean }> {
    return this.request(`/api/reviews/${reviewId}/screening-override`, {
      method: 'PATCH',
      body: JSON.stringify({ overrideReason }),
    });
  }

  // Configuration API

  async getConfig(): Promise<ConfigResponse> {
    return this.request('/api/config');
  }

  async saveConfig(data: ConfigData): Promise<SaveConfigResponse> {
    // Transform legacy property names to API property names
    const transformedData: Record<string, string | undefined> = {};

    // Handle new property names
    if (data.sr_api_key) transformedData.sr_api_key = data.sr_api_key;
    if (data.openrouter_api_key) transformedData.openrouter_api_key = data.openrouter_api_key;
    if (data.webhook_secret) transformedData.webhook_secret = data.webhook_secret;
    if (data.ai_model) transformedData.ai_model = data.ai_model;
    if (data.setup_completed) transformedData.setup_completed = data.setup_completed;

    // US-076: Tiered AI model configuration
    if (data.ai_model_tier_fast) transformedData.ai_model_tier_fast = data.ai_model_tier_fast;
    if (data.ai_model_tier_standard) transformedData.ai_model_tier_standard = data.ai_model_tier_standard;
    if (data.ai_model_tier_premium) transformedData.ai_model_tier_premium = data.ai_model_tier_premium;

    // US-078: Task-to-tier mapping configuration
    if (data.task_tier_embedding) transformedData.task_tier_embedding = data.task_tier_embedding;
    if (data.task_tier_extraction) transformedData.task_tier_extraction = data.task_tier_extraction;
    if (data.task_tier_comparison) transformedData.task_tier_comparison = data.task_tier_comparison;
    if (data.task_tier_reasoning) transformedData.task_tier_reasoning = data.task_tier_reasoning;
    if (data.task_tier_audit) transformedData.task_tier_audit = data.task_tier_audit;

    // Default allowed stages configuration
    if (data.default_allowed_stages) transformedData.default_allowed_stages = data.default_allowed_stages;

    // Data retention configuration (US-066)
    if (data.retention_enabled) transformedData.retention_enabled = data.retention_enabled;
    if (data.retention_inactive_jobs_days) transformedData.retention_inactive_jobs_days = data.retention_inactive_jobs_days;
    if (data.retention_inactive_applications_days) transformedData.retention_inactive_applications_days = data.retention_inactive_applications_days;
    if (data.retention_pii_days) transformedData.retention_pii_days = data.retention_pii_days;
    if (data.retention_reviews_days) transformedData.retention_reviews_days = data.retention_reviews_days;
    if (data.retention_audit_days) transformedData.retention_audit_days = data.retention_audit_days;

    // Handle legacy property names (for backward compatibility with Setup wizard)
    if (data.smartRecruitersApiKey) transformedData.sr_api_key = data.smartRecruitersApiKey;
    if (data.openRouterApiKey) transformedData.openrouter_api_key = data.openRouterApiKey;
    if (data.webhookSecret) transformedData.webhook_secret = data.webhookSecret;

    return this.request('/api/config', {
      method: 'POST',
      body: JSON.stringify(transformedData),
    });
  }

  async validateConfig(data: Partial<ConfigData>): Promise<ValidationResult> {
    return this.request('/api/config/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async testSmartRecruitersConnection(apiKey: string): Promise<ValidationResult> {
    return this.request('/api/config/validate', {
      method: 'POST',
      body: JSON.stringify({ sr_api_key: apiKey }),
    });
  }

  async testOpenRouterConnection(apiKey: string): Promise<ValidationResult> {
    return this.request('/api/config/validate', {
      method: 'POST',
      body: JSON.stringify({ openrouter_api_key: apiKey }),
    });
  }

  // Dead Letter Queue API

  async getDeadLetterEntries(
    params: DeadLetterListParams = {}
  ): Promise<PaginatedResponse<DeadLetterEntry>> {
    const urlParams = new URLSearchParams();
    if (params.page) urlParams.set('page', String(params.page));
    if (params.limit) urlParams.set('limit', String(params.limit));
    if (params.resolved) urlParams.set('resolved', params.resolved);
    if (params.queue) urlParams.set('queue', params.queue);
    if (params.search) urlParams.set('search', params.search);
    return this.request(`/api/dlq?${urlParams.toString()}`);
  }

  async getDeadLetterEntry(id: number): Promise<DeadLetterEntry> {
    return this.request(`/api/dlq/${id}`);
  }

  async resolveDeadLetterEntry(
    id: number,
    data: { resolved: string; notes?: string }
  ): Promise<DeadLetterEntry> {
    return this.request(`/api/dlq/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getDeadLetterStats(): Promise<DeadLetterStats> {
    return this.request('/api/dlq/stats');
  }

  /**
   * Requeue a persisted DLQ record into its original BullMQ queue.
   * Enqueues real work — callers must confirm with the operator first.
   */
  async retryDeadLetterEntry(
    id: number,
    notes?: string
  ): Promise<{ record: DeadLetterEntry; retryJobId: string; retryJobName: string }> {
    return this.request(`/api/dlq/${id}/retry`, {
      method: 'POST',
      body: JSON.stringify(notes ? { notes } : {}),
    });
  }

  // Setup Verification API

  async verifySetup(): Promise<SetupVerifyResponse> {
    return this.request('/api/setup/verify');
  }

  // System Health API

  async getSystemHealth(): Promise<SystemHealthResponse> {
    return this.request('/api/health');
  }

  // US-237: Model Catalog API (Epic 34)
  async getModelCatalog(): Promise<ModelCatalogResponse> {
    const response = await fetch(`${this.baseUrl}/api/model-catalog`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch model catalog: ${response.statusText}`);
    }
    return response.json() as Promise<ModelCatalogResponse>;
  }

  // AI Cost Metrics API

  async getAiCostMetrics(): Promise<AiCostMetrics> {
    return this.request('/api/metrics/json');
  }

  // AI Cost Dashboard API (Task #52)

  async getAICosts(): Promise<AICostDashboardData> {
    return this.request('/api/ai-config/costs');
  }

  // Data Retention Dashboard API - US-070

  async getDataRetentionDashboard(params: {
    page?: number;
    limit?: number;
    entityType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<DataRetentionDashboardResponse> {
    const urlParams = new URLSearchParams();
    if (params.page) urlParams.set('page', String(params.page));
    if (params.limit) urlParams.set('limit', String(params.limit));
    if (params.entityType) urlParams.set('entityType', params.entityType);
    if (params.startDate) urlParams.set('startDate', params.startDate);
    if (params.endDate) urlParams.set('endDate', params.endDate);
    return this.request(`/api/data-retention/dashboard?${urlParams.toString()}`);
  }

  async getDataRetentionAuditLog(params: {
    page?: number;
    limit?: number;
    entityType?: string;
    triggerType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<PaginatedResponse<DataRetentionAuditEntry>> {
    const urlParams = new URLSearchParams();
    if (params.page) urlParams.set('page', String(params.page));
    if (params.limit) urlParams.set('limit', String(params.limit));
    if (params.entityType) urlParams.set('entityType', params.entityType);
    if (params.triggerType) urlParams.set('triggerType', params.triggerType);
    if (params.startDate) urlParams.set('startDate', params.startDate);
    if (params.endDate) urlParams.set('endDate', params.endDate);
    return this.request(`/api/data-retention/audit-log?${urlParams.toString()}`);
  }

  async exportDataRetentionAuditLog(params: {
    entityType?: string;
    triggerType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<Blob> {
    const urlParams = new URLSearchParams();
    if (params.entityType) urlParams.set('entityType', params.entityType);
    if (params.triggerType) urlParams.set('triggerType', params.triggerType);
    if (params.startDate) urlParams.set('startDate', params.startDate);
    if (params.endDate) urlParams.set('endDate', params.endDate);

    const url = `${this.baseUrl}/api/data-retention/audit-log/export?${urlParams.toString()}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.blob();
  }

  async getDataRetentionSummary(): Promise<DataRetentionSummaryResponse> {
    return this.request('/api/data-retention/summary');
  }

  // GDPR Erasure API - US-069

  async searchCandidatesForErasure(
    searchTerm: string,
    limit: number = 20
  ): Promise<{
    data: Array<{
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      srCandidateId: string;
      createdAt: string;
      applicationCount: number;
    }>;
  }> {
    const params = new URLSearchParams({
      q: searchTerm,
      limit: String(limit),
    });
    return this.request(`/api/gdpr/search?${params.toString()}`);
  }

  async getCandidateErasurePreview(candidateId: number): Promise<{
    candidate: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      phone: string | null;
      location: string | null;
      srCandidateId: string;
      createdAt: string;
    };
    dataCounts: {
      applications: number;
      reviews: number;
      piiVaultRecords: number;
      embeddings: number;
      similarityScores: number;
      queueEntries: number;
    };
    warnings: string[];
  }> {
    return this.request(`/api/gdpr/candidate/${candidateId}/preview`);
  }

  async executeGdprErasure(
    candidateId: number,
    reason: 'gdpr_request' | 'retention_policy' | 'manual' = 'gdpr_request',
    forceDelete: boolean = false
  ): Promise<{
    success: boolean;
    deletionRequestId: string;
    certificate: GdprDeletionCertificate;
    deletionSummary: {
      candidates: number;
      applications: number;
      reviews: number;
      piiVault: number;
      resumeEmbeddings: number;
      similarityScores: number;
      queueEntries: number;
      deadLetterAnonymized: number;
      webhookEventsAnonymized: number;
      total: number;
    };
    warnings: string[];
  }> {
    return this.request(`/api/gdpr/candidate/${candidateId}/erasure`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, forceDelete }),
    });
  }

  async getDeletionCertificate(requestId: string): Promise<GdprDeletionCertificate> {
    return this.request(`/api/gdpr/certificate/${requestId}`);
  }

  // Job Criteria API

  async getJobCriteria(jobId: number): Promise<{
    id: number;
    screeningCriteriaText: string | null;
    screeningCriteriaJson: unknown;
    screeningCriteriaVersion: number | null;
    screeningCriteriaHash: string | null;
    screeningCriteriaSource: string | null;
    screeningCriteriaSyncedAt: string | null;
    screeningCriteriaError: string | null;
  }> {
    return this.request(`/api/jobs/${jobId}/criteria`);
  }

  async updateJobCriteria(
    jobId: number,
    criteriaText: string,
    changeReason?: string,
    source?: string
  ): Promise<{
    id: number;
    screeningCriteriaText: string;
    screeningCriteriaVersion: number;
    screeningCriteriaHash: string;
    screeningCriteriaSource: string;
  }> {
    return this.request(`/api/jobs/${jobId}/criteria`, {
      method: 'PUT',
      body: JSON.stringify({ criteriaText, changeReason, source }),
    });
  }

  async syncJobCriteria(jobId: number): Promise<{
    message: string;
    jobId: number;
    srJobId: string;
  }> {
    return this.request(`/api/jobs/${jobId}/criteria/sync`, {
      method: 'POST',
    });
  }

  async generateCriteria(
    jobId: number,
    body: {
      jobDescription: string;
      recruiterContext: string;
      step: 'clarify' | 'generate';
      clarifications?: Array<{ question: string; answer: string }>;
      forceInclude?: string;
    }
  ): Promise<{
    questions?: Array<{ id: string; text: string }>;
    criteriaList?: Array<{ label: string; tier: string; description: string }>;
    criteriaText?: string;
  }> {
    return this.request(`/api/jobs/${jobId}/criteria/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getJobCriteriaChangelog(jobId: number, limit: number = 20): Promise<{
    data: Array<{
      id: number;
      jobId: number;
      version: number;
      criteriaText: string | null;
      criteriaJson: unknown;
      changedBy: string;
      changeReason: string | null;
      createdAt: string;
    }>;
  }> {
    return this.request(`/api/jobs/${jobId}/criteria/changelog?limit=${limit}`);
  }

  async getDeletionAudits(
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<{
    id: number;
    deletionRequestId: string;
    candidateEmailHash: string;
    srCandidateId: string | null;
    status: string;
    totalRecordsDeleted: number;
    reason: string;
    requestedAt: string;
    completedAt: string | null;
    requestedByEmail: string | null;
  }>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    return this.request(`/api/gdpr/audits?${params.toString()}`);
  }

  // Reporting API (US-089)

  async generateReport(
    reportType: ReportType,
    startDate?: string,
    endDate?: string,
    jobId?: number
  ): Promise<ReportGenerateResponse> {
    return this.request('/api/reports/generate', {
      method: 'POST',
      body: JSON.stringify({ reportType, startDate, endDate, jobId }),
    });
  }

  async getReportSnapshots(
    page: number = 1,
    limit: number = 20,
    reportType?: string
  ): Promise<PaginatedResponse<ReportSnapshotSummary>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (reportType) params.set('reportType', reportType);
    return this.request(`/api/reports/snapshots?${params.toString()}`);
  }

  async getReportSnapshot(id: number): Promise<ReportSnapshotFull> {
    return this.request(`/api/reports/snapshots/${id}`);
  }

  async exportReportCsv(id: number): Promise<Blob> {
    const url = `${this.baseUrl}/api/reports/export/${id}`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }
    return response.blob();
  }

  async getReportsDashboard(days: number = 7): Promise<DashboardAnalytics> {
    return this.request(`/api/reports/dashboard?days=${days}`);
  }

  // US-555: batch resume download
  async downloadResumes(applicationIds: number[]): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/applications/resumes/download`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ applicationIds }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail.error || `Download failed: ${response.status}`);
    }
    return response.blob();
  }

  // Notification Settings API

  async getNotificationSettings(jobId: number): Promise<{
    jobId: number;
    members: Array<{
      id: number;
      srUserId: string;
      email: string;
      role: string;
      firstName: string | null;
      lastName: string | null;
      notifyOnReview: boolean;
    }>;
    adminEmail: string;
  }> {
    return this.request(`/api/jobs/${jobId}/notification-settings`);
  }

  async updateNotificationSettings(
    jobId: number,
    members: Array<{ id: number; notifyOnReview: boolean }>
  ): Promise<{
    jobId: number;
    updated: number;
    members: Array<{
      id: number;
      srUserId: string;
      email: string;
      role: string;
      firstName: string | null;
      lastName: string | null;
      notifyOnReview: boolean;
    }>;
  }> {
    return this.request(`/api/jobs/${jobId}/notification-settings`, {
      method: 'PUT',
      body: JSON.stringify({ members }),
    });
  }

  async refreshHiringTeam(jobId: number): Promise<{
    message: string;
    jobId: number;
    count: number;
  }> {
    return this.request(`/api/jobs/${jobId}/refresh-coordinators`, {
      method: 'POST',
    });
  }

  // Chat API

  async createChatConversation(
    applicationId: number,
    modelTier: 'standard' | 'premium' = 'standard'
  ): Promise<{
    id: number;
    applicationId: number;
    modelTier: string;
    estimatedContextTokens: number;
    createdAt: string;
  }> {
    return this.request('/api/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ applicationId, modelTier }),
    });
  }

  async getChatConversations(applicationId: number): Promise<{
    data: Array<{
      id: number;
      applicationId: number;
      title: string | null;
      modelTier: string;
      messageCount: number;
      totalCostUsd: number;
      hasStaleContext: boolean;
      staleReason: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    return this.request(`/api/chat/conversations?applicationId=${applicationId}`);
  }

  async getChatMessages(conversationId: number): Promise<{
    data: Array<{
      id: number;
      conversationId: number;
      role: string;
      content: string;
      modelUsed: string | null;
      costUsd: number | null;
      latencyMs: number | null;
      status: string;
      createdAt: string;
    }>;
  }> {
    return this.request(`/api/chat/conversations/${conversationId}/messages`);
  }

  async getChatSuggestions(conversationId: number): Promise<{
    data: string[];
    commands: Array<{ command: string; description: string }>;
  }> {
    return this.request(`/api/chat/conversations/${conversationId}/suggestions`);
  }

  async saveChatInsight(conversationId: number, messageIds?: number[]): Promise<{
    feedbackId: number;
    condensedContent: string;
    tokenCount: number;
    detectedPII: boolean;
  }> {
    return this.request(`/api/chat/conversations/${conversationId}/save-insight`, {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    });
  }

  async deleteChatConversation(conversationId: number): Promise<{ success: boolean }> {
    return this.request(`/api/chat/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  // Dashboard Jobs API (US-229)

  async getDashboardJobs(): Promise<{ jobs: DashboardJob[] }> {
    const data = await this.request('/api/jobs/dashboard');
    return data as { jobs: DashboardJob[] };
  }

  // Background Signals Engine Phase 1

  async getUnreadSignals(): Promise<{ signals: Array<{
    id: number;
    type: string;
    candidateId: number;
    candidateName: string | null;
    jobId: number;
    jobTitle: string | null;
    payload: Record<string, unknown>;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>; total: number; limit: number; offset: number }> {
    // US-645: the endpoint is bounded by default now. `total` is the true
    // unread count for the badge — `signals.length` is only the page.
    return this.request('/api/signals?status=unread');
  }

  async markSignal(id: number, status: 'read' | 'dismissed'): Promise<{ id: number; status: string }> {
    return this.request(`/api/signals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // AI Accuracy Report

  async getAiAccuracyReport(): Promise<AiAccuracyReportResponse> {
    try {
      return await this.request('/api/reports/ai-accuracy/cached');
    } catch {
      // Fall back to live endpoint if no cached snapshot yet
      return this.request('/api/reports/ai-accuracy');
    }
  }

  // US-230b: Funnel Insights Panel
  async getFunnelInsights(jobId: number): Promise<FunnelInsights> {
    return this.request(`/api/jobs/${jobId}/funnel-insights`);
  }

  // US-243: Provider Credentials API (Epic 34)

  async getProviderCredentials(): Promise<{ credentials: ProviderCredentialMeta[] }> {
    return this.request('/api/provider-credentials');
  }

  async saveProviderCredential(data: {
    provider: string;
    label: string;
    secret: string;
    scopes?: string[];
  }): Promise<{ credential: ProviderCredentialMeta }> {
    return this.request('/api/provider-credentials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async rotateProviderCredential(id: number): Promise<{ credential: ProviderCredentialMeta }> {
    return this.request(`/api/provider-credentials/${id}/rotate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async revokeProviderCredential(id: number): Promise<void> {
    return this.request(`/api/provider-credentials/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async enableProviderCredential(id: number): Promise<{ credential: ProviderCredentialMeta }> {
    return this.request(`/api/provider-credentials/${id}/enable`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async disableProviderCredential(id: number): Promise<{ credential: ProviderCredentialMeta }> {
    return this.request(`/api/provider-credentials/${id}/disable`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async validateProviderKey(provider: string, secret: string): Promise<{ valid: boolean; reason?: string }> {
    // Uses a simple validation endpoint backed by the credential vault
    return this.request('/api/provider-credentials/validate', {
      method: 'POST',
      body: JSON.stringify({ provider, secret }),
    });
  }

  async getProviderPolicy(): Promise<{ policies: ProviderPolicy[] }> {
    return this.request('/api/provider-policies');
  }

  async updateProviderPolicy(provider: string, data: { enabled: boolean }): Promise<{ policy: ProviderPolicy }> {
    return this.request(`/api/provider-policies/${provider}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Generic HTTP methods for ad-hoc API calls (US-260 org management hooks)

  async get<T>(url: string): Promise<T> {
    return this.request<T>(url);
  }

  async post<T = unknown>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T = unknown>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export class for testing
export { ApiClient };

// US-271: Setup wizard standalone API functions (no auth required — pre-login)

export async function getSetupStatus(): Promise<{ firstRun: boolean }> {
  const res = await fetch(`${BASE_URL}/api/setup/status`);
  if (!res.ok) throw new Error('Failed to fetch setup status');
  return res.json();
}

export async function registerFirstOrg(orgName: string, email: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orgName, email }),
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? body?.message ?? 'Registration failed');
  }
}

export async function testSrApiKey(
  srApiKey: string,
): Promise<{ valid: boolean; error?: string }> {
  const res = await fetch(`${BASE_URL}/api/config/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sr_api_key: srApiKey }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? 'Validation request failed');
  return body.results?.sr_api_key ?? { valid: false, error: 'No result returned' };
}

export async function saveSrApiKey(srApiKey: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sr_api_key: srApiKey }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? 'Failed to save API key');
  }
}
